import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { GroupStats } from '../../../../shared/models/agency-stats/AgencyStatsResponse';
import { getChartThemeColors } from '../../../../shared/utils/chartTheme';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';

const CHART_COLOR_ACTIVE = '#40b681';   // --green-100
const CHART_COLOR_PAUSED = '#4c7dea';   // --primary-ux-400
const CHART_COLOR_PENDING = '#e0c951';  // --yellow-200
const CHART_COLOR_DELETED = '#959596';  // --grey-ux-500

// Telefon: grubość poziomego słupka; z niej (4 słupki + przerwa na grupę) liczona jest wysokość wykresu.
const MOBILE_BAR_WIDTH = 8;

interface AgencyStatsBarChartProps {
  groups: GroupStats[];
}

const AgencyStatsBarChart: React.FC<AgencyStatsBarChartProps> = ({ groups }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const barOption = useMemo<EChartsOption>(() => {
    const labels = groups.map((g) => (g.groupId === null ? t('ungrouped') : g.groupName));
    const chartColors = getChartThemeColors();
    // Telefon: ten sam wykres obrócony — grupy jedna pod drugą z nazwą przy osi, poziome słupki — zamiast
    // kolumn z obróconymi, ucinanymi nazwami.
    const categoryAxis = {
      type: 'category' as const,
      data: labels,
      inverse: isMobile,
      axisLabel: {
        fontFamily: 'Inter,sans-serif',
        fontSize: isMobile ? 12 : 11,
        interval: 0,
        rotate: !isMobile && labels.length > 6 ? 30 : 0,
        overflow: 'truncate' as const,
        width: isMobile ? 96 : 80,
      },
      axisLine: { show: false },
      axisTick: { show: false },
    };
    const valueAxis = {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: chartColors.splitLine } },
      minInterval: 1,
    };
    const bar = (name: string, data: number[], color: string) => ({
      name,
      type: 'bar' as const,
      data,
      itemStyle: { color, borderRadius: isMobile ? [0, 2, 2, 0] : [2, 2, 0, 0] },
      ...(isMobile ? { barWidth: MOBILE_BAR_WIDTH, barGap: '25%' } : { barMaxWidth: 32 }),
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        appendToBody: true,
        borderColor: chartColors.tooltipBorder,
        backgroundColor: chartColors.tooltipBg,
        textStyle: { color: chartColors.tooltipText },
      },
      legend: { show: false },
      grid: isMobile
        ? { top: 8, bottom: 24, left: 0, right: 16, containLabel: true }
        : { top: 24, bottom: 40, left: 40, right: 16 },
      xAxis: isMobile ? valueAxis : categoryAxis,
      yAxis: isMobile ? categoryAxis : valueAxis,
      series: [
        bar(t('agencyStatsActive'), groups.map((g) => g.activePrompts), CHART_COLOR_ACTIVE),
        bar(t('agencyStatsPaused'), groups.map((g) => g.pausedPrompts), CHART_COLOR_PAUSED),
        bar(t('agencyStatsPending'), groups.map((g) => g.pendingPrompts), CHART_COLOR_PENDING),
        bar(t('agencyStatsDeletedPrompts'), groups.map((g) => g.deletedPrompts), CHART_COLOR_DELETED),
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, t, theme, isMobile]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('agencyStatsPromptsPerGroup')}
        </div>
      </div>
      {/* Telefon: wysokość z liczby grup, bez stałej wysokości pudełka wykresu. */}
      <div className={`stats-box${isMobile ? '' : ' stats-box--chart'}`}>
        <div className="stats-box__content">
          <ReactECharts
            key={theme}
            option={barOption}
            style={{
              height: isMobile
                ? `${Math.max(1, groups.length) * (4 * (MOBILE_BAR_WIDTH + 2) + 20) + 32}px`
                : '294px',
              width: '100%',
            }}
            notMerge
          />
        </div>
      </div>
    </div>
  );
};

export default AgencyStatsBarChart;
